import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import StarRating from './StarRating';
import FilterPopover from './FilterPopover'; // Issue #38
import SortPopover from './SortPopover'; // Issue #40
import CreatePost from './CreatePost';
import './CourseReview.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu } from './shared';
import { useLogout } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAds } from '../contexts/AdContext'; // Issue #311: Ad initialization
import { AdBanner } from './ads'; // Issue #311: Banner ads

// Issue #76: Pagination constant
const LIMIT = 10;

// Issue #219: Session storage key for scroll position restoration
const SCROLL_POSITION_KEY = 'courseReviewScrollPosition';

function CourseReview({ initialFilters, onFiltersChange, initialSortBy, onSortChange }) {
  const navigation = useNavigation();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();
  const { initFromUserData } = useAds(); // Issue #311: Initialize ads

  const [courses, setCourses] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // Issue #76: Total number of courses (not just loaded)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [studyPrograms, setStudyPrograms] = useState([]); // Issue #179: Available study programs for user's university

  // Issue #76: Pagination state
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const canLoadMore = useRef(false); // Issue #76: Prevent immediate load-more on initial render
  const [universityName, setUniversityName] = useState('');
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // Issue #38: Filter state - use initialFilters from props if provided
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(initialFilters || {
    degree: '',
    study_programs: [],
    language: '',
    semester: '',
    // Issue #78: Rating-related filters
    has_reviews: false,
    my_reviews: false,
    min_rating: 0
  });

  // Issue #40: Sort state - use initialSortBy from props if provided
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState(initialSortBy || '');

  // Issue #228: Search query state for course name search
  // searchQuery = input field value, activeSearch = committed search that triggers API
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // Issue #311: Delay banner ad to prevent flash during scroll restoration
  const [bannerReady, setBannerReady] = useState(false);


  // Issue #38: Fetch courses with filters
  // Issue #76: Added pagination support with reset parameter
  const fetchCourses = useCallback(async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
        setHasMore(true);
        canLoadMore.current = false; // Issue #76: Disable load-more during reset
      } else {
        setIsLoadingMore(true);
      }

      // Get user info to display university name (only on initial load)
      // Issue #179: Also fetch study programs for user's university
      if (reset) {
        const userResponse = await axios.get('/api/user/me');
        setUserData(userResponse.data);
        setUniversityName(userResponse.data.enrolled_university || '');
        // Issue #311: Initialize AdContext for banner ads
        initFromUserData(userResponse.data);

        // Issue #179: Fetch study programs for user's university
        try {
          const programsResponse = await axios.get('/api/study-programs');
          setStudyPrograms(programsResponse.data || []);
        } catch (programsError) {
          console.error('Error fetching study programs:', programsError);
          setStudyPrograms([]);
        }
      }

      // Build query parameters for filters
      const params = new URLSearchParams();
      if (activeFilters.degree) {
        params.append('degree', activeFilters.degree);
      }
      if (activeFilters.study_programs && activeFilters.study_programs.length > 0) {
        params.append('study_programs', activeFilters.study_programs.join(','));
      }
      if (activeFilters.language) {
        params.append('language', activeFilters.language);
      }
      if (activeFilters.semester) {
        params.append('semester', activeFilters.semester);
      }

      // Issue #78: Add rating-related filters
      if (activeFilters.has_reviews) {
        params.append('has_reviews', 'true');
      }
      if (activeFilters.my_reviews) {
        params.append('my_reviews', 'true');
      }
      if (activeFilters.min_rating) {
        params.append('min_rating', activeFilters.min_rating.toString());
      }

      // Issue #40: Add sort parameter
      if (sortBy) {
        params.append('sort_by', sortBy);
      }

      // Issue #228: Add search parameter (only when user clicks search icon)
      if (activeSearch && activeSearch.trim()) {
        params.append('search', activeSearch.trim());
      }

      // Issue #76: Add pagination parameters
      params.append('limit', LIMIT.toString());
      params.append('offset', reset ? '0' : offset.toString());

      // Fetch courses with filters and pagination
      const url = `/api/courses?${params.toString()}`;
      const coursesResponse = await axios.get(url);
      const { courses: newCourses, total_count } = coursesResponse.data;

      // Issue #76: Update courses - replace on reset, append otherwise
      if (reset) {
        setCourses(newCourses);
        setTotalCount(total_count); // Update total count on reset
      } else {
        setCourses(prev => [...prev, ...newCourses]);
      }

      // Issue #76: Check if there are more courses to load
      if (newCourses.length < LIMIT) {
        setHasMore(false);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(t('courseReview.errorLoading'));
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      // Issue #76: Enable load-more after a delay to prevent immediate trigger on initial render
      if (!canLoadMore.current) {
        setTimeout(() => {
          canLoadMore.current = true;
        }, 500);
      }
    }
  }, [activeFilters, sortBy, activeSearch, offset]);

  // Issue #76: Load more courses when scrolling
  const loadMoreCourses = useCallback(() => {
    if (!isLoadingMore && hasMore && !loading && canLoadMore.current) {
      setOffset(prev => prev + LIMIT);
    }
  }, [isLoadingMore, hasMore, loading]);

  // Initial fetch and refetch when filters/sort change
  useEffect(() => {
    fetchCourses(true);
  }, [activeFilters, sortBy, activeSearch]); // Re-fetch when filters, sort, or search changes

  // Issue #76: Fetch more when offset changes (but not on initial load)
  useEffect(() => {
    if (offset > 0) {
      fetchCourses(false);
    }
  }, [offset]);

  // Issue #76: IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !isLoadingMore) {
          loadMoreCourses();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, isLoadingMore, loadMoreCourses]);

  // Sync filters back to App.js to persist across navigation
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(activeFilters);
    }
  }, [activeFilters, onFiltersChange]);

  // Sync sort option back to App.js to persist across navigation and page refresh
  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortBy);
    }
  }, [sortBy, onSortChange]);

  // Issue #219: Restore scroll position when returning from course detail page
  useEffect(() => {
    if (!loading && courses.length > 0) {
      const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
      if (savedScrollPosition) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
          // Clear the saved position after restoring
          sessionStorage.removeItem(SCROLL_POSITION_KEY);
        });
      }
    }
  }, [loading, courses.length]);

  // Issue #311: Delay banner ad to prevent flash during scroll restoration
  useEffect(() => {
    // Wait for scroll restoration to complete before showing banner
    const timer = setTimeout(() => setBannerReady(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const handleSeeAllReviews = (course) => {
    // Issue #219: Save scroll position before navigating to course detail
    sessionStorage.setItem(SCROLL_POSITION_KEY, window.scrollY.toString());
    navigation.onNavigateToCourseDetail?.(course);
  };

  const handlePostCreated = () => {
    // Post created - user can view it in main feed
    console.log('Post created successfully');
  };

  // Issue #38: Filter handlers
  const handleOpenFilter = () => {
    setIsFilterOpen(true);
  };

  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const handleApplyFilter = (filters) => {
    setActiveFilters(filters);
  };

  // Issue #38: Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters.degree) count++;
    if (activeFilters.study_programs && activeFilters.study_programs.length > 0) {
      count += activeFilters.study_programs.length;
    }
    if (activeFilters.language) count++;
    if (activeFilters.semester) count++;
    // Issue #78: Count rating-related filters
    if (activeFilters.has_reviews) count++;
    if (activeFilters.my_reviews) count++;
    if (activeFilters.min_rating) count++;
    return count;
  };

  // Issue #75: Reset filters from outside popover
  const handleResetFilters = (e) => {
    e.stopPropagation(); // Prevent opening the filter popover
    setActiveFilters({
      degree: '',
      study_programs: [],
      language: '',
      semester: '',
      // Issue #78: Reset rating-related filters
      has_reviews: false,
      my_reviews: false,
      min_rating: 0
    });
  };

  // Issue #40: Sort handlers
  const handleOpenSort = () => {
    setIsSortOpen(true);
  };

  const handleCloseSort = () => {
    setIsSortOpen(false);
  };

  const handleApplySort = (newSortBy) => {
    setSortBy(newSortBy);
  };

  // Issue #139: Reset sort criteria
  const handleResetSort = (e) => {
    e.stopPropagation(); // Prevent opening the sort popover
    setSortBy('');
  };

  return (
    <div className="cr-page">
      <div className="cr-container">
        {/* Header - Using shared AppHeader component */}
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        >
          <div className="cr-title-section">
            <button className="cr-title">{t('courseReview.title')}</button>
          </div>
        </AppHeader>

        {/* Disclaimer */}
        <div className="cr-disclaimer">
          <span className="cr-info-icon">ⓘ</span>
          <p className="cr-disclaimer-text">
            {t('courseReview.disclaimer')}
          </p>
        </div>

        {/* Issue #228: Search field for course name search */}
        {/* Search triggers only when user clicks search icon or presses Enter */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder={t('courseReview.searchPlaceholder') || 'Kurs suchen...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setActiveSearch(searchQuery);
                }
              }}
              aria-label={t('courseReview.searchAriaLabel') || 'Kurse durchsuchen'}
            />
            <button
              type="button"
              className="search-button"
              onClick={() => setActiveSearch(searchQuery)}
              aria-label={t('courseReview.searchButtonAriaLabel') || 'Suche starten'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Issue #38: Filter section with badge */}
        {/* Issue #75: Reset button moved outside popover */}
        {/* Issue #115: Changed div to button for keyboard accessibility */}
        <div className="cr-filter-section">
          <button type="button" className="cr-filter-trigger" onClick={handleOpenFilter} aria-label={t('filter') || 'Filter'} aria-expanded={isFilterOpen}>
            <span className="cr-filter-icon" aria-hidden="true">⚙️</span>
            <span className="cr-filter-label">{t('filter')}</span>
            {getActiveFilterCount() > 0 && (
              <span className="cr-filter-badge" aria-label={`${getActiveFilterCount()} ${t('courseReview.filter.activeFilters') || 'active filters'}`}>{getActiveFilterCount()}</span>
            )}
          </button>
          {getActiveFilterCount() > 0 && (
            <button className="cr-filter-reset-button" onClick={handleResetFilters}>
              {t('courseReview.filter.reset')}
            </button>
          )}
        </div>

        {/* Issue #40: Sort section */}
        {/* Issue #115: Changed div to button for keyboard accessibility */}
        {/* Issue #136: Display correct sort criterion below button */}
        {/* Issue #139: Added reset button for sort criteria */}
        <div className="cr-sort-wrapper">
          <div className="cr-sort-header">
            <button type="button" className="cr-sort-section" onClick={handleOpenSort} aria-label={t('courseReview.sort.sortBy') || 'Sort'} aria-expanded={isSortOpen}>
              <span className="cr-sort-icon" aria-hidden="true">📊</span>
              <span className="cr-sort-label">{t('courseReview.sort.sortBy')}</span>
            </button>
            {sortBy && (
              <button className="cr-sort-reset-button" onClick={handleResetSort} aria-label={t('courseReview.sort.clearAriaLabel') || 'Clear sort criteria'}>
                {t('courseReview.sort.clear')}
              </button>
            )}
          </div>
          {sortBy && (
            <div className="cr-sort-chips" role="status" aria-live="polite" aria-label={t('courseReview.sort.appliedSorts') || 'Applied sort criteria'}>
              {sortBy.split(',').filter(s => s).map(sort => (
                <span key={sort} className="cr-sort-chip">
                  {sort === 'review_count_desc' && t('courseReview.sort.reviewsDescending')}
                  {sort === 'rating_asc' && t('courseReview.sort.ratingAscending')}
                  {sort === 'rating_desc' && t('courseReview.sort.ratingDescending')}
                  {sort === 'name_asc' && t('courseReview.sort.nameAsc')}
                  {sort === 'name_desc' && t('courseReview.sort.nameDesc')}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Course count display */}
        {!loading && !error && (
          <div className="cr-course-count">
            {totalCount} {totalCount === 1 ? t('courseReview.courseCountSingular') : t('courseReview.courseCountPlural')}
          </div>
        )}

        {/* Issue #311: Banner ad above first course (delayed to prevent flash during scroll restore) */}
        {bannerReady && <AdBanner slot="course-review-top" />}

        {/* Courses List */}
        <div className="cr-courses-list">
          {loading && (
            <div className="cr-loading">{t('courseReview.loadingCourses')}</div>
          )}

          {error && (
            <div className="cr-error">{error}</div>
          )}

          {!loading && !error && courses.length === 0 && (
            <div className="cr-empty">{t('courseReview.noCourses')}</div>
          )}

          {!loading && !error && courses.map((course) => (
            <div
              key={course.id}
              className="cr-course-card"
              onClick={() => handleSeeAllReviews(course)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSeeAllReviews(course); } }}
              role="button"
              tabIndex={0}
              aria-label={`${course.name}, ${course.review_count} ${t('courseReview.reviews')}`}
            >
              {/* Course Name */}
              <div className="cr-course-header">
                <span className="cr-course-name">{course.name}</span>
              </div>

              {/* Rating Section */}
              <div className="cr-rating-section">
                <StarRating rating={course.average_rating} reviewCount={course.review_count} />
              </div>
            </div>
          ))}

          {/* Issue #76: Load more indicator */}
          {isLoadingMore && (
            <div className="cr-loading-more">{t('courseReview.loadingMore')}</div>
          )}

          {/* Issue #76: Sentinel element for infinite scroll */}
          {!loading && hasMore && !isLoadingMore && courses.length > 0 && (
            <div ref={loadMoreRef} className="cr-load-more-sentinel" />
          )}

          {/* Issue #76: End of list message */}
          {!loading && !hasMore && courses.length > 0 && (
            <div className="cr-end-of-list">{t('courseReview.allLoaded')}</div>
          )}
        </div>
      </div>


      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="home"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      {/* Issue #101: Use NavigationContext for dedicated create pages */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* CreatePost Component */}
      {universityName && (
        <CreatePost
          userName={universityName}
          onPostCreated={handlePostCreated}
        />
      )}

      {/* Issue #38: Filter Popover */}
      {/* Issue #179: Pass dynamic study programs for user's university */}
      {isFilterOpen && (
        <FilterPopover
          onClose={handleCloseFilter}
          onApply={handleApplyFilter}
          initialFilters={activeFilters}
          availablePrograms={studyPrograms}
        />
      )}

      {/* Issue #40: Sort Popover */}
      {isSortOpen && (
        <SortPopover
          onClose={handleCloseSort}
          onApply={handleApplySort}
          initialSortBy={sortBy}
        />
      )}

    </div>
  );
}

export default CourseReview;
